import { CircleDashed } from 'lucide-react'
import { useEffect, useMemo } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { useGetDentalPlans } from 'components/hooks/member_onboarding_v2/useGetDentalPlans'
import { useGetVisionPlans } from 'components/hooks/member_onboarding_v2/useGetVisionPlans'

import { AncillaryDetails } from './AncillaryDetails'
import { SmartBudgetPlan } from './Plan'
import PlanCard from './PlanCard/PlanCard'
import { dentalPlanToPlanData, visionPlanToPlanData } from '../../smartBudgetUtils'
import { type CoverageType, type DentalPlan, type VisionPlan } from '../../types'

interface SmartBudgetAncillaryPlanProps {
  enrollmentEventId: string,
  coverageType: 'dental' | 'vision',
  viewingCoverageType: CoverageType | null,
  setViewingCoverageType: (planType: CoverageType | null) => void,
  remainingAllowance: number,
  selectedPlan: DentalPlan | VisionPlan | null,
  onSelectPlan: (plan: DentalPlan | VisionPlan | null) => void,
  planCodeInMemberCart: string | null | undefined,
  planCannotBeChanged: boolean,
}

export function SmartBudgetAncillaryPlan ({
  enrollmentEventId,
  coverageType,
  viewingCoverageType,
  setViewingCoverageType,
  remainingAllowance,
  selectedPlan,
  onSelectPlan,
  planCodeInMemberCart,
  planCannotBeChanged,
}: SmartBudgetAncillaryPlanProps) {
  const isVisionPlan = coverageType === 'vision'
  const webhook = isVisionPlan ? useGetVisionPlans : useGetDentalPlans

  const {
    data,
    isLoading,
    error,
  } = webhook({
    enrollmentEventId,
  })

  const allPlans = data?.data

  const sortedPlans = useMemo(() => {
    if (!allPlans) return []
    return [...allPlans].sort((a, b) => a.premium.subunits - b.premium.subunits)
  }, [allPlans])

  const initialPlan = useMemo(() => {
    if (!planCodeInMemberCart) return sortedPlans[0]
    return sortedPlans.find((plan) => plan.plan_code === planCodeInMemberCart) || sortedPlans[0]
  }, [sortedPlans, planCodeInMemberCart])

  useEffect(() => {
    if (!selectedPlan) {
      onSelectPlan(initialPlan)
    }
  }, [selectedPlan, initialPlan, onSelectPlan])

  if (isLoading) {
    return (
      <PlanCard.Loading />
    )
  }

  if (error) {
    return (
      <PlanCard.Error coverageType={coverageType} error={error as GrapeAPIErrorType} />
    )
  }

  if (sortedPlans.length === 0) {
    return (
      <PlanCard.Empty>
        <div className='flex flex-row items-center justify-center gap-4'>
          <CircleDashed className='w-12 h-12 text-gray-300' />
          <p className='text-gray-500'>Your employer is offering you coverage for {coverageType} plans, but unfortunately no plans are available in you area.</p>
        </div>
      </PlanCard.Empty>
    )
  }

  return (
    <SmartBudgetPlan
      allPlans={sortedPlans}
      selectedPlan={selectedPlan || initialPlan}
      getPlanKey={(plan) => plan.plan_code}
      planToPlanData={(plan) => isVisionPlan ? visionPlanToPlanData(plan as VisionPlan) : dentalPlanToPlanData(plan as DentalPlan)}
      planToPlanPremium={(plan) => plan.premium}
      remainingAllowance={remainingAllowance}
      coverageType={coverageType}
      viewingCoverageType={viewingCoverageType}
      setViewingCoverageType={setViewingCoverageType}
      onSelectPlan={(plan) => onSelectPlan(plan)}
      renderPlanDetails={(plan) => <AncillaryDetails ancillaryPlan={plan} />}
      popoverTitle={`Other available ${coverageType} plans:`}
      planCannotBeChanged={planCannotBeChanged}
    />
  )
}

SmartBudgetAncillaryPlan.displayName = 'SmartBudget.AncillaryPlan'
